<template>
  <div class="dashboard">
    <header class="dashboard-header">
      <div class="header-top">
        <div>
          <div class="station-name">{{ stationName }}</div>
          <div class="date-label">{{ todayFormatted }}</div>
        </div>
        <button class="icon-btn" @click="$router.push('/history')">
          <font-awesome-icon icon="clock-rotate-left" />
          <span>History</span>
        </button>
      </div>
    </header>

    <div class="fail-banner" v-if="failedToday.length">
      <font-awesome-icon icon="triangle-exclamation" />
      {{ failedToday.length }} out-of-range reading{{ failedToday.length > 1 ? 's' : '' }} today
    </div>

    <div class="stats-row">
      <div class="stat-card" v-for="probe in probeSummary" :key="probe.id">
        <div class="stat-label">{{ probe.label }}</div>
        <div class="stat-count">{{ probe.count }}</div>
        <div class="stat-sub">readings today</div>
      </div>
    </div>

    <button class="record-btn" @click="$router.push('/record')">
      <font-awesome-icon icon="thermometer-half" />
      Record Temperature
    </button>

    <div class="readings-section">
      <h3>Today's Readings</h3>
      <div v-if="todaysReadings.length === 0" class="empty-state">
        No readings recorded today yet.
      </div>
      <div
        v-for="reading in todaysReadings"
        :key="reading.id"
        :class="['reading-card', reading.pass ? 'pass' : 'fail']"
      >
        <div class="reading-main">
          <span class="reading-temp">{{ reading.temperature }}°C</span>
          <span class="reading-probe">{{ probeLabel(reading.probeType) }}</span>
          <span :class="['reading-badge', reading.pass ? 'badge-pass' : 'badge-fail']">
            {{ reading.pass ? 'PASS' : 'FAIL' }}
          </span>
        </div>
        <div class="reading-meta">
          <span>{{ formatTime(reading.timestamp) }}</span>
          <span v-if="reading.notes" class="reading-notes">{{ reading.notes }}</span>
        </div>
        <div class="reading-action" v-if="!reading.pass && reading.correctiveAction">
          <strong>Action:</strong> {{ reading.correctiveAction }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { api } from "@monolith/helpers";

const PROBE_TYPES = [
  { id: 'chiller', label: 'Chiller', min: 0, max: 5 },
  { id: 'freezer', label: 'Freezer', min: -30, max: -18 },
  { id: 'hot_hold', label: 'Hot Hold', min: 60, max: 100 },
  { id: 'food_probe', label: 'Food Probe', min: 75, max: 100 },
  { id: 'ambient', label: 'Ambient', min: null, max: null },
];

export default {
  name: "TemperatureDashboard",
  data() {
    return {
      probeTypes: PROBE_TYPES,
    }
  },
  computed: {
    stationName() {
      return localStorage.getItem("station") || "Temperature Monitor";
    },
    todayFormatted() {
      return new Date().toLocaleDateString('en-NZ', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      });
    },
    todaysReadings() {
      return this.$store.getters.todaysReadings;
    },
    failedToday() {
      return this.$store.getters.failedReadingsToday;
    },
    probeSummary() {
      const today = this.todaysReadings;
      return PROBE_TYPES.filter(p => p.id !== 'ambient').map(p => ({
        ...p,
        count: today.filter(r => r.probeType === p.id).length
      })).filter(p => p.count > 0);
    }
  },
  async created() {
    await this.loadReadings();
  },
  methods: {
    async loadReadings() {
      try {
        const readings = await api.get(`/api/temperature`);
        this.$store.dispatch('setReadings', readings);
      } catch (e) {
        // fall through to any locally cached readings
      }
    },
    probeLabel(id) {
      const probe = PROBE_TYPES.find(p => p.id === id);
      return probe ? probe.label : id;
    },
    formatTime(ts) {
      return new Date(ts).toLocaleTimeString('en-NZ', {
        hour: '2-digit', minute: '2-digit'
      });
    }
  }
}
</script>

<style scoped lang="scss">
.dashboard {
  max-width: 600px;
  margin: 0 auto;
  padding-bottom: 2rem;
}

.dashboard-header {
  background-color: var(--banner-colour);
  color: #fff;
  padding: 1rem 1.25rem;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.station-name {
  font-size: 1.2rem;
  font-weight: bold;
}

.date-label {
  font-size: 0.85rem;
  opacity: 0.8;
  margin-top: 0.2rem;
}

.icon-btn {
  background: transparent;
  border: 1px solid rgba(255,255,255,0.4);
  color: #fff;
  padding: 0.5rem 0.75rem;
  border-radius: 0.4rem;
  font-size: 0.85rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  margin-left: 0;

  svg { font-size: 1.1rem; }
  span { font-size: 0.7rem; }
}

.fail-banner {
  background-color: var(--fail-colour);
  color: #fff;
  padding: 0.75rem 1.25rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.stats-row {
  display: flex;
  gap: 0.75rem;
  padding: 1rem 1.25rem 0;
  overflow-x: auto;
}

.stat-card {
  background-color: var(--secondary-colour);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  min-width: 90px;
  text-align: center;
  flex-shrink: 0;
}

.stat-label {
  font-size: 0.75rem;
  font-weight: bold;
  color: var(--text-colour);
  opacity: 0.7;
}

.stat-count {
  font-size: 1.8rem;
  font-weight: bold;
  color: var(--action-colour);
}

.stat-sub {
  font-size: 0.65rem;
  opacity: 0.6;
}

.record-btn {
  display: block;
  width: calc(100% - 2.5rem);
  margin: 1.25rem 1.25rem 0;
  padding: 1.25rem;
  font-size: 1.1rem;
  font-weight: bold;
  background-color: var(--action-colour);
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;

  svg { font-size: 1.3rem; }
}

.readings-section {
  padding: 0 1.25rem;

  h3 {
    margin: 1.25rem 0 0.75rem;
    font-size: 1rem;
    font-weight: bold;
    opacity: 0.7;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
}

.empty-state {
  text-align: center;
  padding: 2rem;
  opacity: 0.5;
  font-size: 0.9rem;
}

.reading-card {
  background-color: var(--secondary-colour);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 0.6rem;
  border-left: 4px solid transparent;

  &.pass { border-left-color: var(--pass-colour); }
  &.fail { border-left-color: var(--fail-colour); }
}

.reading-main {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.reading-temp {
  font-size: 1.3rem;
  font-weight: bold;
}

.reading-probe {
  flex: 1;
  font-size: 0.9rem;
  opacity: 0.8;
}

.reading-badge {
  font-size: 0.7rem;
  font-weight: bold;
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
  color: #fff;

  &.badge-pass { background-color: var(--pass-colour); }
  &.badge-fail { background-color: var(--fail-colour); }
}

.reading-meta {
  display: flex;
  gap: 1rem;
  font-size: 0.8rem;
  opacity: 0.6;
  margin-top: 0.35rem;
}

.reading-notes {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reading-action {
  font-size: 0.8rem;
  margin-top: 0.35rem;
  color: var(--fail-colour);
}
</style>
